import * as React from 'react'

import { faAngleDown, faCheck } from '@document-crunch/pro-regular-svg-icons'

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@dc-shared-web/components/shadcn/command'
import { useInfiniteScroll } from '@dc-shared-web/hooks/utils/useInfiniteScroll'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dc-shared-web/components/shadcn/popover'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { cn } from '@dc-shared-web/lib'
import Spinner from '@dc-shared-web/components/base/Spinner'

export type ComboBoxOption = {
  label: string | null
  value: string
}

export type ComboBoxSection = {
  label: string
  options: ComboBoxOption[]
}

type BaseComboBoxProps = {
  variant?: 'default' | 'dark'
  placeholder?: string
  triggerPlaceholder?: string
  selectedId?: string
  onChange?: (value: string | undefined) => void
  widthClass?: string
  className?: string
  savedClassName?: string
  search?: string
  setSearch?: React.Dispatch<React.SetStateAction<string>>
  onScroll?: () => void
  // If an API (or other source) is returning filtered options, then don't filter a second time
  externalFilterUsed?: boolean
  isLoading?: boolean
  isSaving?: boolean
  disabled?: boolean
  datatestid?: string
  popoverClassName?: string
  iconClassName?: string
  allowNew?: boolean
  selectedValue?: string
  showChevron?: boolean
  maxInputLength?: number
  trigger?: React.ReactNode
  alwaysShowOptions?: string[]
}

export type ComboBoxProps = BaseComboBoxProps &
  (
    | { options: ComboBoxOption[]; sections?: never }
    | { sections: ComboBoxSection[]; options?: never }
  )

// Section Header Component
const SectionHeader = ({ label }: { label: string }) => (
  <div className='tw-sticky tw-top-0 tw-z-30 tw-bg-white tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-text-neutral-500 tw-border-b tw-border-neutral-200 tw-mt-0'>
    {label}
  </div>
)

export function Combobox({
  variant = 'default',
  placeholder,
  triggerPlaceholder,
  selectedId,
  options,
  sections,
  onChange,
  widthClass,
  className,
  savedClassName,
  search: propsSearch,
  setSearch: propsSetSearch,
  onScroll: propsOnScroll = () => {},
  externalFilterUsed,
  isLoading,
  isSaving,
  disabled,
  popoverClassName,
  iconClassName,
  datatestid,
  selectedValue,
  allowNew,
  showChevron = true,
  maxInputLength,
  trigger,
  alwaysShowOptions,
  ...props
}: ComboBoxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchState, setSearchState] = React.useState('')

  const search = propsSearch ?? searchState
  const setSearch = propsSetSearch ?? setSearchState

  const onScroll = useInfiniteScroll(50, propsOnScroll)

  const onItemSelect = (selectedValue: string) => {
    onChange?.(selectedValue === selectedId ? undefined : selectedValue)
    setOpen(false)
  }

  React.useEffect(() => {
    if (!open) {
      setSearch('')
    }
  }, [open, setSearch])

  // Normalize data structure - convert sections to flat options for backward compatibility
  const allOptions = React.useMemo(() => {
    if (sections) {
      return sections.flatMap((section) => section.options)
    }
    return options || []
  }, [sections, options])

  const filteredOptions = React.useMemo(() => {
    if (externalFilterUsed) {
      return sections ? sections : options ? [{ label: '', options }] : []
    }

    const filterOptions = (options: ComboBoxOption[]) => {
      return search
        ? options.filter(
            (option) =>
              (option.label || '')
                .toLowerCase()
                .includes(search.toLowerCase()) &&
              option.value !== selectedValue
          )
        : options.filter((option) => option.value !== selectedValue)
    }

    if (sections) {
      // Filter sections and their options
      const filteredSections = sections
        .map((section) => ({
          ...section,
          options: filterOptions(section.options),
        }))
        .filter((section) => section.options.length > 0)

      // Handle alwaysShowOptions across sections
      if (alwaysShowOptions && alwaysShowOptions.length > 0) {
        const alwaysShowSet = new Set(alwaysShowOptions)
        const alwaysShownOptions = allOptions.filter((option) =>
          alwaysShowSet.has(option.value)
        )

        // Add always shown options to the first section or create a new section
        if (alwaysShownOptions.length > 0) {
          const alwaysShowSection = {
            label: 'Always Show',
            options: alwaysShownOptions,
          }
          filteredSections.unshift(alwaysShowSection)
        }
      }

      return filteredSections
    }

    // Original flat options logic
    const filtered = filterOptions(allOptions)
    const alwaysShowSet = new Set(alwaysShowOptions)
    const alwaysShownOptions = allOptions.filter((option) =>
      alwaysShowSet.has(option.value)
    )

    return [
      {
        label: '',
        options: [...new Set([...alwaysShownOptions, ...filtered])],
      },
    ]
  }, [
    search,
    sections,
    options,
    externalFilterUsed,
    selectedValue,
    alwaysShowOptions,
    allOptions,
  ])

  const getTriggerText = () => {
    if (isSaving) {
      return 'Saving...'
    }
    if (selectedValue) {
      return selectedValue
    }
    if (selectedId) {
      return allOptions.find((option) => option.value === selectedId)?.label
    }
    return triggerPlaceholder ?? placeholder
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ? (
          trigger
        ) : (
          <Button
            data-testid={datatestid}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn(
              'tw-w-[200px] tw-justify-between tw-font-normal tw-text-muted-foreground',
              variant === 'dark' &&
                'tw-bg-neutral-100 hover:tw-bg-neutral-200 tw-text-2xs tw-justify-center tw-text-neutral-700 tw-max-h-10',
              widthClass,
              selectedId && 'tw-text-neutral-700',
              className,
              selectedValue || selectedId ? savedClassName : '',
              isSaving &&
                'tw-opacity-50 tw-bg-transparent tw-cursor-not-allowed hover:tw-bg-transparent tw-text-neutral-700 !tw-border-0 !tw-outline-none !tw-ring-transparent !tw-ring-0'
            )}
            {...props}
          >
            <span
              className={cn(
                'tw-truncate',
                variant === 'dark' && showChevron && 'tw-mr-4'
              )}
            >
              {getTriggerText()}
            </span>
            {showChevron && (
              <Icon
                icon={faAngleDown}
                className={iconClassName}
                color={
                  variant === 'dark' ? IconColor.darkPrimary : IconColor.primary
                }
              />
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className={cn(
          'tw-p-0 tw-relative',
          !popoverClassName?.includes('tw-w-') &&
            'tw-popover-content-width-same-as-its-trigger',
          popoverClassName
        )}
      >
        <Command shouldFilter={false}>
          <CommandInput
            maxLength={maxInputLength ?? 256}
            placeholder={`Search ${placeholder?.toLowerCase()} ...`}
            onInput={(e) => setSearch(e.currentTarget.value)}
            className='placeholder:tw-text-neutral-400'
          />

          <CommandList>
            {isLoading && (
              <div className='tw-h-[86px]'>
                <Spinner className='tw-absolute tw-left-[42%] tw-top-[54%]' />
              </div>
            )}

            {!allowNew && !isLoading && filteredOptions.length === 0 && (
              <div className='tw-text-center tw-text-sm tw-my-8'>
                No results found.
              </div>
            )}

            {allowNew && search && (
              <CommandItem
                value={search}
                onSelect={() => {
                  onChange?.(search)
                  setOpen(false)
                }}
              >
                {`Add new: ${search}`}
              </CommandItem>
            )}

            <CommandGroup
              className='tw-max-h-64 tw-overflow-auto tw-relative tw-pt-0'
              onScroll={onScroll}
            >
              {filteredOptions.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {section.label && <SectionHeader label={section.label} />}
                  {section.options.map((option, optionIndex) => (
                    <CommandItem
                      key={`${sectionIndex}-${optionIndex}`}
                      value={option.value}
                      // we can't use the value that comes from onSelect prop here because it converts all values to lowercase
                      // and we need to preserve the case of the original value, this is an issue with cmdk
                      // https://github.com/pacocoursey/cmdk/issues/150
                      onSelect={() => onItemSelect(option.value)}
                      data-testid={`${datatestid}-command-item`}
                    >
                      <Icon
                        icon={faCheck}
                        className={cn(
                          'tw-mr-2',
                          selectedId === option.value
                            ? 'tw-opacity-100'
                            : 'tw-opacity-0'
                        )}
                        color={IconColor.tertiary}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </React.Fragment>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
